import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../../lib/firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}

/* ---------------- mobile select helpers ---------------- */
function useIsMobile(breakpoint = 520) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= breakpoint;
  });

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

function MobileSheetSelect({
  label,
  value,
  onChange,
  options, // [{value,label}]
  disabled = false,
  placeholder = "Select…",
}) {
  const [open, setOpen] = useState(false);

  const selectedLabel =
    options.find((o) => String(o.value) === String(value))?.label || "";

  const close = () => setOpen(false);

  return (
    <div style={{ display: "grid", gap: 6 }}>
      {label ? <div style={S.label}>{label}</div> : null}

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(true)}
        style={{
          ...S.input,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: disabled ? "rgba(2,6,23,0.03)" : "rgba(255,255,255,0.95)",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <span style={{ color: selectedLabel ? "#0f172a" : "rgba(15,23,42,0.55)" }}>
          {selectedLabel || placeholder}
        </span>
        <span style={{ fontWeight: 1000 }}>▾</span>
      </button>

      {open ? (
        <div
          onClick={close}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.45)",
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              background: "#fff",
              borderTopLeftRadius: 18,
              borderTopRightRadius: 18,
              padding: 12,
              borderTop: "1px solid rgba(15,23,42,0.10)",
              boxShadow: "0 -20px 60px rgba(15,23,42,0.15)",
              maxHeight: "70vh",
              overflow: "auto",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
                marginBottom: 10,
              }}
            >
              <div style={{ fontWeight: 1000 }}>{label || "Select"}</div>
              <button
                type="button"
                onClick={close}
                style={{
                  border: "1px solid rgba(15,23,42,0.10)",
                  background: "rgba(255,255,255,0.95)",
                  borderRadius: 12,
                  padding: "8px 10px",
                  fontWeight: 950,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              {options.map((opt) => {
                const active = String(opt.value) === String(value);
                return (
                  <button
                    key={String(opt.value)}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      close();
                    }}
                    style={{
                      border: "1px solid rgba(15,23,42,0.10)",
                      background: active ? "#0f172a" : "#fff",
                      color: active ? "#fff" : "#0f172a",
                      borderRadius: 14,
                      padding: "12px 12px",
                      fontWeight: 950,
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function ReportScreen({ goBack }) {
  const u = auth.currentUser;
  const meEmail = useMemo(() => safe(u?.email), [u]);

  const isMobile = useIsMobile(520);

  const [type, setType] = useState("BUG");
  const [reason, setReason] = useState("");
  const [targetId, setTargetId] = useState("");
  const [details, setDetails] = useState("");

  const [sending, setSending] = useState(false);
  const [done, setDone] = useState("");

  async function submit() {
    setDone("");

    const r = safe(reason);
    const d = safe(details);
    const tId = safe(targetId);

    if (!r) {
      setDone("Please add a short reason (title).");
      return;
    }
    if (!d) {
      setDone("Please describe the issue.");
      return;
    }

    try {
      setSending(true);

      await addDoc(collection(db, "reports"), {
        uid: u?.uid || "",
        email: meEmail,

        // ✅ matches AdminReportsScreen display
        type: safe(type) || "OTHER",
        reason: r,
        targetId: tId, // optional

        details: d,

        status: "OPEN",

        // ✅ admin uses/updates these
        assignedTo: "",
        resolvedBy: "",
        resolutionNote: "",

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setReason("");
      setTargetId("");
      setDetails("");
      setType("BUG");
      setDone("Report submitted ✅");
    } catch (e) {
      console.error(e);
      setDone(e?.message ? `Failed ❌ ${e.message}` : "Failed to submit ❌ Try again.");
    } finally {
      setSending(false);
    }
  }

  const typeOptions = useMemo(
    () => [
      { value: "BUG", label: "Bug" },
      { value: "SCAM", label: "Scam / Fraud" },
      { value: "ABUSE", label: "Abuse" },
      { value: "OTHER", label: "Other" },
    ],
    []
  );

  return (
    <div style={S.page}>
      <div style={S.header}>
        <button onClick={goBack} style={S.backBtn}>
          ←
        </button>
        <div>
          <div style={S.title}>Report</div>
          <div style={S.subtitle}>Tell us what happened</div>
        </div>
      </div>

      <div style={S.card}>
        {/* Type */}
        {isMobile ? (
          <MobileSheetSelect
            label="Type"
            value={type}
            onChange={(v) => setType(String(v))}
            options={typeOptions}
            placeholder="Select type"
          />
        ) : (
          <>
            <div style={S.label}>Type</div>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              style={S.input}
            >
              <option value="BUG">Bug</option>
              <option value="SCAM">Scam / Fraud</option>
              <option value="ABUSE">Abuse</option>
              <option value="OTHER">Other</option>
            </select>
          </>
        )}

        <div style={{ height: 10 }} />

        <div style={S.label}>Reason (short title)</div>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="E.g. Seller not responding / Payment issue"
          style={S.input}
        />

        <div style={{ height: 10 }} />

        <div style={S.label}>Target ID (optional)</div>
        <input
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          placeholder="User ID / Store ID / Product ID / Order ID"
          style={S.input}
        />

        <div style={{ height: 10 }} />

        <div style={S.label}>Details</div>
        <textarea
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          placeholder="Explain what happened…"
          style={S.textarea}
        />

        {done ? <div style={S.note}>{done}</div> : null}

        <button onClick={submit} style={S.btn} disabled={sending}>
          {sending ? "Submitting…" : "Submit report"}
        </button>

        <div style={S.smallTip}>We’ll review it as soon as possible.</div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background:
      "radial-gradient(900px 500px at 20% 10%, rgba(99,102,241,0.22), transparent 60%), radial-gradient(900px 500px at 80% 20%, rgba(236,72,153,0.18), transparent 60%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 55%, #f8fafc 100%)",
    fontFamily:
      'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial',
    color: "#0f172a",
  },
  header: {
    maxWidth: 760,
    margin: "0 auto 12px",
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  backBtn: {
    width: 44,
    height: 44,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.85)",
    boxShadow: "0 12px 30px rgba(15, 23, 42, 0.06)",
    cursor: "pointer",
    fontWeight: 900,
  },
  title: { fontWeight: 950, fontSize: 20, letterSpacing: -0.3 },
  subtitle: {
    marginTop: 2,
    fontWeight: 800,
    fontSize: 12,
    color: "rgba(15,23,42,0.55)",
  },

  card: {
    maxWidth: 760,
    margin: "0 auto",
    borderRadius: 20,
    padding: 14,
    background: "rgba(255,255,255,0.85)",
    border: "1px solid rgba(15,23,42,0.10)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
  },
  label: { fontWeight: 950, color: "#0f172a", marginBottom: 6, fontSize: 12 },
  input: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 900,
    background: "rgba(255,255,255,0.95)",
  },
  textarea: {
    width: "100%",
    minHeight: 150,
    padding: "12px 12px",
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 900,
    background: "rgba(255,255,255,0.95)",
    resize: "vertical",
    lineHeight: 1.4,
  },
  note: {
    marginTop: 10,
    padding: 10,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(2,6,23,0.03)",
    fontWeight: 900,
    color: "rgba(15,23,42,0.75)",
  },
  btn: {
    marginTop: 12,
    width: "100%",
    padding: "12px 14px",
    borderRadius: 16,
    border: "none",
    background: "linear-gradient(135deg, #6366f1 0%, #ec4899 100%)",
    color: "white",
    fontWeight: 950,
    cursor: "pointer",
  },
  smallTip: {
    marginTop: 10,
    fontWeight: 850,
    fontSize: 12,
    color: "rgba(15,23,42,0.55)",
  },
};
